import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { publications } from '../../data/publicationsData';
import AnimatedSectionTitle from '../AnimatedSectionTitle';
import Grainient from '../Grainient/Grainient';
import './PublicationsList.css';

// Matches the HomeV2 panel's own slide-in transition duration: rows stay
// hidden while the panel is still sliding, then reveal a beat later, same
// reasoning as ProjectsPage's deferred card reveal (just without video
// decode cost to worry about here).
const PANEL_SETTLE_DELAY = 650;
const ROW_STAGGER = 0.08;

interface PublicationsListProps {
  theme?: string;
  hideHeader?: boolean;
}

const PublicationsList: React.FC<PublicationsListProps> = ({ theme = 'dark', hideHeader = false }) => {
  const isDark = theme === 'dark';
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const rows = rowRefs.current.filter((el): el is HTMLDivElement => el !== null);
    gsap.set(rows, { opacity: 0, x: 80 });

    const timer = setTimeout(() => {
      gsap.to(rows, {
        opacity: 1,
        x: 0,
        duration: 0.55,
        ease: 'power3.out',
        stagger: ROW_STAGGER,
      });
    }, PANEL_SETTLE_DELAY);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="pub-section">
      {!hideHeader && (
        <>
          <AnimatedSectionTitle title="Publications" className="pub-page-title" />
          <p className="pub-page-subtitle">Peer-reviewed and preprint research, first-authored where noted.</p>
        </>
      )}

      <div className="pub-list">
        {publications.map((pub, index) => (
          <div
            className="pub-row"
            key={index}
            ref={(el) => { rowRefs.current[index] = el; }}
            style={{ '--pub-accent': pub.color || 'var(--text-color)' } as React.CSSProperties}
          >
            <Grainient
              color1={isDark ? '#1a1a1a' : '#ffffff'}
              color2={isDark ? '#1a1a1a' : '#ffffff'}
              color3={pub.color || '#FF4141'}
              grainAmount={0.06}
              warpStrength={0.8}
              timeSpeed={0.15}
            />
            <div className="pub-row-content">
              {pub.previewImage && (
                <div className="pub-preview">
                  <img src={pub.previewImage} alt={`${pub.title} preview`} />
                </div>
              )}

              <div className="pub-body">
                {pub.logo && (
                  <div className="pub-logo">
                    <img src={pub.logo} alt={`${pub.publisher} logo`} className="pub-publisher-logo" />
                  </div>
                )}

                <h3 className="pub-title">{pub.title}</h3>
                <div className="pub-meta">
                  {pub.publisher}
                  {pub.conference && <> · {pub.conference}</>}
                  {' · '}{pub.date}
                </div>
                <div className="pub-authors">{pub.authors.join(', ')}</div>

                {pub.tags.length > 0 && (
                  <div className="pub-tags">
                    {pub.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className={`pub-tag ${tagIndex === 0 ? 'accent' : ''}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <p className="pub-abstract">{pub.abstract}</p>

                {pub.doi && (
                  <a href={pub.doi} target="_blank" rel="noopener noreferrer" className="pub-link">
                    view paper
                    <span className="pub-arrow">→</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PublicationsList;
